import { useEffect, useState } from "react";
import { getProducts } from "api/Products";

/**
 * Resolves the productId the calculator should use for pricing and RFQ
 * creation. Priority:
 *   1. REACT_APP_DEFAULT_MULCH_PRODUCT_ID env var (explicit override)
 *   2. First non-addon product in the company's catalog (auto-detect)
 *
 * If neither is available, returns an error so the UI can show a helpful
 * message instead of failing silently.
 *
 * `enabled` (Session 78): the products catalog is staff-only, and the
 * client mode of the editor does not price at all — it measures. Passing
 * false skips the fetch entirely rather than letting it 403 and surface
 * "Failed to load products" to a client who never needed a product.
 */
export function useDefaultMulchProduct(enabled = true) {
  const envProductId =
    process.env.REACT_APP_DEFAULT_MULCH_PRODUCT_ID || "";

  const [productId, setProductId] = useState<string>(envProductId);
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(!envProductId && enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setProductId("");
      setProducts([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (envProductId) {
      setProductId(envProductId);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getProducts({ query: { recordsPer: 100 } })
      .then((res: any) => {
        if (cancelled) return;

        const items = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : [];
        const nonAddon = items.filter((p: any) => !p?.addon);

        if (nonAddon.length === 0) {
          setError(
            "No mulch products configured for this company. Add one under Products before using the calculator.",
          );
          setProductId("");
          setProducts([]);
        } else {
          setProducts(
            nonAddon.map((p: any) => ({ id: p.id, name: p.name || "Product" })),
          );
          setProductId(nonAddon[0].id);
          setError(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load products.");
        setProductId("");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [envProductId, enabled]);

  return { productId, products, loading, error };
}
